import { XCloseIcon } from "@/icons";
import { Badge } from "@/kit/ui/badge";
import { cn } from "@/utils/tailwind";
import { useEffect, useId, useState } from "react";
import { graphql, useFragment } from "react-relay";
import {
  AnnouncementFragment$key,
  AnnouncementKind,
} from "./__generated__/AnnouncementFragment.graphql";
import Markdown from "./Markdown";

const AnnouncementFragment = graphql`
  fragment AnnouncementFragment on Query
  @argumentDefinitions(targetPath: { type: "String!" }) {
    announcementByTargetPath(targetPath: $targetPath) {
      targetPath
      expiresAt
      message
      active
      badge
      kind
    }
  }
`;

const KIND_LIVE = Object.freeze<
  Record<AnnouncementKind, "assertive" | "polite">
>({
  INFO: "polite",
  WARNING: "polite",
  DANGER: "assertive",
  SUCCESS: "polite",
});

const BANNER = Object.freeze<
  Record<AnnouncementKind, { bar: string; text: string; close: string }>
>({
  INFO: {
    bar: "bg-info-50 border-b border-info-100",
    text: "text-info-900",
    close: "text-info-400 hover:text-info-700",
  },
  WARNING: {
    bar: "bg-warning-50 border-b border-warning-100",
    text: "text-warning-900",
    close: "text-warning-400 hover:text-warning-700",
  },
  DANGER: {
    bar: "bg-error-50 border-b border-error-100",
    text: "text-error-900",
    close: "text-error-400 hover:text-error-700",
  },
  SUCCESS: {
    bar: "bg-success-50 border-b border-success-100",
    text: "text-success-900",
    close: "text-success-400 hover:text-success-700",
  },
});

const PREFIX = "aqora_announcement_dismissed" as const;

type StorageKey = `${typeof PREFIX}::${string}`;

function storageKey(targetPath: string): StorageKey {
  return `${PREFIX}::${targetPath}`;
}

function isDismissed(targetPath: string): boolean {
  try {
    return localStorage.getItem(storageKey(targetPath)) === "true";
  } catch {
    return false;
  }
}

function dismiss(targetPath: string): void {
  try {
    localStorage.setItem(storageKey(targetPath), "true");
  } catch {
    // in ssr
  }
}

interface Props {
  query: AnnouncementFragment$key;
}

export function Announcement({ query }: Props) {
  const { announcementByTargetPath: announcement } = useFragment(
    AnnouncementFragment,
    query,
  );

  const messageId = useId();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!announcement?.targetPath || !announcement?.active) {
      setVisible(false);
      return;
    }
    setVisible(!isDismissed(announcement.targetPath));
  }, [announcement]);

  if (!visible) return;
  if (!announcement?.active) return;

  const bannerStyles = BANNER[announcement.kind];
  const live = KIND_LIVE[announcement.kind];

  function handleDismiss() {
    dismiss(announcement!.targetPath);
    setVisible(false);
  }

  return (
    <div
      role="region"
      aria-label="Announcement"
      aria-live={live}
      aria-atomic="true"
      className={cn(
        "w-full flex items-center justify-between gap-3 px-4 py-1 text-xs",
        bannerStyles.bar,
      )}
    >
      <div
        id={messageId}
        className="flex items-center justify-center gap-2 min-w-0"
      >
        {announcement.badge && (
          <Badge shape="rounded" size="sm" aria-hidden="true">
            {announcement.badge}
          </Badge>
        )}
        <Markdown className={cn("truncate leading-snug", bannerStyles.text)}>
          {announcement.message}
        </Markdown>
      </div>
      <button
        type="button"
        aria-label="Dismiss announcement"
        aria-describedby={messageId}
        onClick={handleDismiss}
        className={cn(
          "shrink-0 rounded p-0.5 hover:cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2",
          bannerStyles.close,
        )}
      >
        <XCloseIcon className="size-4" aria-hidden="true" />
      </button>
    </div>
  );
}
